/**
 * HoneyGrid Brand Colors
 * Based on the design system guidelines
 */

export const brandColors = {
	// Primary brand colors
	primary: '#3A2E2F', // Dark brown
	primaryContrast: '#FFFDFD', // Almost white
	
	// Application colors
	primaryAction: '#621984', // Purple
	primaryLight: '#81479C', // Light purple
	
	// Hex grid colors
	hexGrid: '#878DDD', // Periwinkle blue
	
	// Gradient colors
	gradient: {
		cyan: '#6EC3C9',
		tealGreen: '#7AC5A6',
		yellowGreen: '#9BC77C',
		yellow: '#C4CA5C',
		goldenYellow: '#EDCC4B',
		orangeYellow: '#F5B968',
		peach: '#F19E82',
		pink: '#E8849C',
		purplePink: '#D26EB5',
		purple: '#B15FCD',
		bluePurple: '#8B5CE5',
	},
} as const

/**
 * Get the brand gradient as a CSS linear-gradient string
 * @param angle - The angle of the gradient (default: 135deg)
 * @returns CSS gradient string
 */
export const getBrandGradient = (angle: number = 135): string => {
	return `linear-gradient(
		${angle}deg,
		${brandColors.gradient.cyan} 0%,
		${brandColors.gradient.tealGreen} 10%,
		${brandColors.gradient.yellowGreen} 20%,
		${brandColors.gradient.yellow} 30%,
		${brandColors.gradient.goldenYellow} 40%,
		${brandColors.gradient.orangeYellow} 50%,
		${brandColors.gradient.peach} 60%,
		${brandColors.gradient.pink} 70%,
		${brandColors.gradient.purplePink} 80%,
		${brandColors.gradient.purple} 90%,
		${brandColors.gradient.bluePurple} 100%
	)`
}

/**
 * Get a radial gradient for special effects
 * @returns CSS radial gradient string
 */
export const getBrandRadialGradient = (): string => {
	return `radial-gradient(
		ellipse at center,
		${brandColors.gradient.orangeYellow} 0%,
		${brandColors.gradient.yellowGreen} 25%,
		${brandColors.gradient.cyan} 50%,
		${brandColors.gradient.purple} 75%,
		${brandColors.gradient.bluePurple} 100%
	)`
}

/**
 * Get a subtle gradient overlay with transparency
 * @param opacity - The opacity of the gradient (0-1)
 * @returns CSS gradient string with transparency
 */
export const getBrandGradientOverlay = (opacity: number = 0.1): string => {
	return `linear-gradient(
		135deg,
		rgba(110, 195, 201, ${opacity}) 0%,
		rgba(155, 199, 124, ${opacity}) 25%,
		rgba(245, 185, 104, ${opacity}) 50%,
		rgba(232, 132, 156, ${opacity}) 75%,
		rgba(177, 95, 205, ${opacity}) 100%
	)`
}

/**
 * CSS class names for gradient effects
 */
export const gradientClasses = {
	gradient: 'honeygrid-gradient',
	radial: 'honeygrid-gradient-radial',
	animated: 'honeygrid-gradient-animated',
	overlay: 'honeygrid-gradient-overlay',
	text: 'honeygrid-gradient-text',
	border: 'honeygrid-gradient-border',
} as const
